"use client"

import { useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { UserRole } from '../constants/user-roles'
import { DASHBOARD_ROUTE } from '../constants/pages-routes'
import { Session } from '../types/session.type'
import { useIsGroupContact } from '@/features/stm/hooks/use-is-group-contact'
import { useSession } from '@/features/auth/context/session.context'

// Region-aware roots for route extraction
const REGION_AWARE_ROOTS = new Set(["home", "reports", "homeowners", "work-orders", "volunteers", "base", "migration", "partners", "admin"])

// Role-based route access mapping
const routeAccessMap: Record<string, UserRole[]> = {
    // /reports is accessible to all authenticated users; sensitive report drill-downs handle their own role-based privacy.
    '/home-owners': [UserRole.ADMIN, UserRole.BASE_LEADER, UserRole.PARTNER],
    // Volunteer/TeamLeader/Partner need isGroupContact check (done in component)
    '/volunteers': [UserRole.ADMIN, UserRole.BASE_LEADER, UserRole.VOLUNTEER, UserRole.TEAM_LEADER, UserRole.PARTNER],
    // /partners is accessible to all authenticated users (read-only for non-admin/base-leader)
    // Not listed here so all authenticated users can access
    // /partners/create is restricted to ADMIN and BASE_LEADER
    '/partners/create': [UserRole.ADMIN, UserRole.BASE_LEADER],
    // /partners/[id]/edit is restricted to ADMIN and BASE_LEADER
    '/partners/edit': [UserRole.ADMIN, UserRole.BASE_LEADER],
    '/admin': [UserRole.ADMIN, UserRole.BASE_LEADER],
    '/tools-supplies': [UserRole.ADMIN, UserRole.BASE_LEADER],
    '/migration': [UserRole.ADMIN, UserRole.BASE_LEADER],
    // Add more restricted routes as needed
}

// Helper function to extract route from region-aware pathname
// e.g., "/swannanoa-valley/partners" -> "/partners"
// e.g., "/swannanoa-valley/partners/create" -> "/partners/create"
function extractRouteFromPathname(pathname: string): string {
    const segments = pathname.split('/').filter(Boolean)
    
    // If pathname starts with a region slug (not in REGION_AWARE_ROOTS), extract the route
    if (segments.length >= 2 && !REGION_AWARE_ROOTS.has(segments[0])) {
        // Assume first segment is region slug, return the rest of the path
        return '/' + segments.slice(1).join('/')
    }
    
    // Otherwise return the pathname as-is
    return pathname
}

const GROUP_CONTACT_ROLES = [UserRole.VOLUNTEER, UserRole.TEAM_LEADER, UserRole.PARTNER]

// Helper function to check if user has access to current route
function hasAccessToRoute(userRole: UserRole, pathname: string, isGroupContact?: boolean, inAnyGroup?: boolean): boolean {
    const route = extractRouteFromPathname(pathname)

    // Supporters cannot access Work Orders
    if (route.startsWith('/work-orders') && userRole === UserRole.Supporter) {
        return false
    }

    // Volunteer/TeamLeader/Partner need to be in a group (contact or member) to access /volunteers
    if (route.startsWith('/volunteers') && GROUP_CONTACT_ROLES.includes(userRole)) {
        return (isGroupContact === true || inAnyGroup === true)
    }
    
    // Check if the current path requires specific roles
    const requiredRoles = routeAccessMap[route]
    
    // If no specific roles required, allow access
    if (!requiredRoles) {
        return true
    }
    
    // Check if user's role is in the allowed roles
    return requiredRoles.includes(userRole)
}

interface RoleGuardProps {
    session?: Session
    children: React.ReactNode
}

export function RoleGuard({ session: sessionProp, children }: RoleGuardProps) {
    const { session: contextSession } = useSession()
    const session = sessionProp ?? contextSession
    const { pathname } = useLocation()
    const navigate = useNavigate()
    const { isGroupContact, inAnyGroup, isLoading: isGroupContactLoading } = useIsGroupContact()
    const [hasAccess, setHasAccess] = useState(false)
    
    useEffect(() => {
        if (!session) return
        const route = extractRouteFromPathname(pathname)
        const needsGroupContactCheck = route.startsWith('/volunteers') && GROUP_CONTACT_ROLES.includes(session.role as UserRole)
        const waitForCheck = needsGroupContactCheck && isGroupContactLoading

        if (waitForCheck) {
            setHasAccess(false)
            return
        }

        const access = hasAccessToRoute(session.role as UserRole, pathname, isGroupContact, inAnyGroup)
        setHasAccess(access)
        
        if (!access) {
            navigate(DASHBOARD_ROUTE.dashboard, { replace: true })
        }
    }, [pathname, session?.role, navigate, isGroupContact, inAnyGroup, isGroupContactLoading])
    
    if (!session) return null
    
    const isChecking = (() => {
        const route = extractRouteFromPathname(pathname)
        const needsGroupContactCheck = route.startsWith('/volunteers') && GROUP_CONTACT_ROLES.includes(session.role as UserRole)
        return needsGroupContactCheck ? isGroupContactLoading : false
    })()

    // Show loading state while checking or if no access
    if (isChecking || !hasAccess) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Checking access...</p>
                </div>
            </div>
        )
    }
    
    return <>{children}</>
} 